import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import { ref, onValue, remove } from 'firebase/database';
import AddUserForm from './AddUserForm';
import TrainingProgress from './TrainingProgress';
import { sendWelcomeInvite } from '../services/welcomeEmail';
import { update } from 'firebase/database';
import OnboardingChecklist, { markStep } from './OnboardingChecklist';
import { onboardingStatus, needsOnboarding } from '../config/onboarding';

const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const styles = {
  panel: { backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderRadius: '10px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' },
  title: { fontSize: '20px', fontWeight: 700, margin: 0, color: '#1a1a1a', fontFamily: FONT },
  bigButton: { padding: '14px 24px', background: 'linear-gradient(180deg, #f6e7b4 0%, #d9b357 42%, #b98d2e 58%, #e8cc7a 100%)', color: '#1a1a1a', border: '1px solid #b8903a', borderRadius: '6px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px', fontFamily: FONT },
  th: { textAlign: 'left', fontSize: '12px', color: '#8C6D1F', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '12px 8px', borderBottom: '2px solid #d8c9a8', fontWeight: 700 },
  td: { padding: '14px 8px', borderBottom: '1px solid #efe6d2', color: '#1a1a1a', verticalAlign: 'middle' },
  editBtn: { background: '#ffffff', border: '1px solid #8C6D1F', color: '#8C6D1F', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, marginRight: '8px' },
  deleteBtn: { background: '#ffffff', border: '1px solid #b00020', color: '#b00020', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  notice: { backgroundColor: '#eef7ee', border: '1px solid #2e7d32', borderRadius: '6px', padding: '12px', marginBottom: '18px', color: '#2e7d32', fontSize: '14px', fontWeight: 600, fontFamily: FONT },
  error: { backgroundColor: '#fbeeee', border: '1px solid #b00020', borderRadius: '6px', padding: '12px', marginBottom: '18px', color: '#b00020', fontSize: '14px', fontFamily: FONT },
  empty: { color: '#7f7f7f', fontSize: '14px', margin: 0, fontFamily: FONT },
  tableWrap: { overflowX: 'auto' },
};

const nameOf = (u) => {
  const p = u.profile || {};
  if (p.firstName) return `${p.firstName} ${p.lastName || ''}`.trim();
  return u.displayName || u.email || 'Unnamed user';
};

export default function UsersSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState('list'); // 'list' | 'new' | 'edit' | 'training'
  const [trainingUser, setTrainingUser] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [onbFor, setOnbFor] = useState(null); // uid whose checklist is open
  const [onbOnly, setOnbOnly] = useState(false); // filter: agents with incomplete onboarding

  useEffect(() => {
    const unsubscribe = onValue(
      ref(database, 'users'),
      (snap) => {
        const val = snap.val() || {};
        const list = Object.entries(val)
          .map(([uid, u]) => ({ uid, ...u }))
          .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
        setUsers(list);
        setLoaded(true);
      },
      (err) => {
        console.error('Could not load users:', err);
        setError('Could not load the user list. Check that your account can read users.');
        setLoaded(true);
      }
    );
    return () => unsubscribe();
  }, []);

  const startEdit = (u) => {
    setNotice('');
    setError('');
    setEditing(u);
    setMode('edit');
  };

  const removeUser = async (u) => {
    const name = nameOf(u);
    const ok = window.confirm(
      `Delete ${name}? This removes their profile, commission settings, role, and everything stored under their account, including their transactions. Their login stays in Firebase Authentication until it is removed there. This cannot be undone.`
    );
    if (!ok) return;
    try {
      setError('');
      await remove(ref(database, `users/${u.uid}`));
      setNotice(`${name} was removed from Resi.`);
    } catch (err) {
      console.error('Could not delete user:', err);
      setError(`Could not delete ${name}. ${err.message || ''}`.trim());
    }
  };

  // Shared by the row button and the onboarding checklist. On success, ticks the "Welcome email sent" step.
  const sendWelcomeFor = async (u) => {
    const to = u.email; if (!to) { setError('This user has no sign-in email.'); return { ok: false, reason: 'no sign-in email' }; }
    if (!window.confirm(`Send the welcome email (sign in with the temporary password welcome) to ${to}? Only works for accounts that have never signed in.`)) return { ok: false, reason: 'cancelled' };
    setNotice(''); setError('');
    const r = await sendWelcomeInvite(u.uid, to, nameOf(u));
    if (r.ok) {
      setNotice(`Welcome email sent to ${to}.`);
      markStep(u.uid, 'welcomeEmail', true, user?.uid, { via: r.via || null }).catch((e) => setError(`Sent, but the checklist was not updated: ${e.message}`));
    } else if (r.reason === 'already_signed_in') setError(`${to} has already signed in, so no welcome email was sent. They can use Forgot password.`);
    else setError(`Email not sent (${r.reason || 'error'}). Text them instead: ${r.instructions || ''}`);
    return r;
  };

  const shownUsers = onbOnly ? users.filter((u) => needsOnboarding(u) && !onboardingStatus(u).complete) : users;
  const onbUser = onbFor ? users.find((x) => x.uid === onbFor) : null;

  const backToList = (message) => {
    setMode('list');
    setEditing(null);
    if (message) setNotice(message);
  };

  const openTraining = (u) => {
    setNotice('');
    setError('');
    setTrainingUser(u);
    setMode('training');
  };

  if (mode === 'training' && trainingUser) {
    return <TrainingProgress uid={trainingUser.uid} name={nameOf(trainingUser)} onBack={() => { setMode('list'); setTrainingUser(null); }} />;
  }

  if (mode === 'new') {
    return <AddUserForm onDone={backToList} />;
  }
  if (mode === 'edit' && editing) {
    return <AddUserForm existing={editing} onDone={backToList} />;
  }

  return (
    <div style={styles.panel}>
      <div style={styles.headerRow}>
        <h2 style={styles.title}>Users {loaded ? `(${users.length})` : ''}</h2>
        <button type="button" onClick={() => { setNotice(''); setError(''); setMode('new'); }} style={styles.bigButton}>
          + New User
        </button>
      </div>

      <label style={{ fontSize: 13, display: 'inline-block', marginBottom: 12 }}>
        <input type="checkbox" checked={onbOnly} onChange={(e) => setOnbOnly(e.target.checked)} /> Onboarding: show only agents with an incomplete checklist
      </label>
      {onbUser && <OnboardingChecklist user={onbUser} name={nameOf(onbUser)} adminUid={user?.uid} onSendWelcome={sendWelcomeFor} onClose={() => setOnbFor(null)} />}
      {notice && <div style={styles.notice}>{notice}</div>}
      {error && <div style={styles.error}>{error}</div>}

      {!loaded ? (
        <p style={styles.empty}>Loading users...</p>
      ) : users.length === 0 ? (
        <p style={styles.empty}>No users yet. Click + New User to add one.</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Organization</th>
                <th style={styles.th}>Onboarding</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {shownUsers.map((u) => (
                <tr key={u.uid}>
                  <td style={styles.td}>{nameOf(u)}</td>
                  <td style={styles.td}>{u.role || '—'}</td>
                  <td style={styles.td}>{u.email || u.profile?.contactEmail || '—'}</td>
                  <td style={styles.td}>{u.profile?.phone || '—'}</td>
                  <td style={styles.td}>{u.organization || '—'}</td>
                  <td style={styles.td}>{needsOnboarding(u) ? (() => { const o = onboardingStatus(u); return (
                    <button type="button" onClick={() => setOnbFor(u.uid)} title="Open the onboarding checklist" style={{ ...styles.editBtn, background: o.complete ? '#eef7ee' : '#fff6d6', borderColor: o.complete ? '#2e7d32' : '#b8903a', color: o.complete ? '#2e7d32' : '#8C6D1F' }}>Onboarding {o.done}/{o.total}</button>
                  ); })() : '—'}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => openTraining(u)} style={styles.editBtn}>Training</button>
                    <button type="button" onClick={() => startEdit(u)} style={styles.editBtn}>Edit</button>
                    <button type="button" style={styles.editBtn} title="Sends the branded welcome email: sign in at resisystem.com/login with the temporary password welcome, then set their own. Never-signed-in accounts only. Logged in the Messages Log." onClick={() => sendWelcomeFor(u)}>Send welcome email</button>
                    <label style={{ fontSize: 12, marginRight: 8 }} title="Only tick after this person's email is confirmed for sending (they clicked the confirmation link). Then client emails go out from their own address; otherwise from the team address as '<Name> via RESIDENCE'.">
                      <input type="checkbox" checked={u.emailSenderVerified === true} onChange={(e) => update(ref(database, `users/${u.uid}`), { emailSenderVerified: e.target.checked }).catch((err) => setError(err.message))} /> Verified sender
                    </label>
                    {user?.uid !== u.uid && (
                      <button type="button" onClick={() => removeUser(u)} style={styles.deleteBtn}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
